import { useState, useEffect, useCallback } from 'react';
import { getIncidentProvider } from '@/lib/incidents/factory';
import type { Incident, CreateIncidentParams, IncidentStatus } from '@/lib/incidents/types';
import { useOptimisticMutation } from '@/hooks/useOptimisticMutation';

export function useIncidents(statusFilter?: IncidentStatus) {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const provider = getIncidentProvider();

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await provider.listIncidents({ status: statusFilter, limit: 50 });
    setIncidents(data);
    setLoading(false);
  }, [provider, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async (params: CreateIncidentParams) => {
    const incident = await provider.createIncident(params);
    await refresh();
    return incident;
  };

  // Snapshot used for rollback if the mutation fails.
  const [snapshots, setSnapshots] = useState<Record<string, Incident | undefined>>({});

  const applyStatus = (id: string, status: IncidentStatus) => {
    setSnapshots((prev) => {
      if (prev[id]) return prev;
      const found = incidents.find((i) => i.id === id);
      return { ...prev, [id]: found };
    });
    setIncidents((prev) =>
      prev.map((i) =>
        i.id === id
          ? {
              ...i,
              status,
              ...(status === 'resolved' ? { resolvedAt: new Date().toISOString() } : {}),
            }
          : i,
      ),
    );
  };

  const rollbackStatus = (id: string) => {
    setSnapshots((prev) => {
      const snap = prev[id];
      if (snap) setIncidents((curr) => curr.map((i) => (i.id === id ? snap : i)));
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const ackMutation = useOptimisticMutation<string>({
    mutationFn: (id) => provider.acknowledgeIncident(id),
    getKey: (id) => id,
    applyOptimistic: (id) => applyStatus(id, 'acknowledged'),
    rollback: (id) => rollbackStatus(id),
    successMessage: 'Incidente reconocido',
    errorMessage: 'No se pudo reconocer el incidente',
  });

  const resolveMutation = useOptimisticMutation<string>({
    mutationFn: (id) => provider.resolveIncident(id),
    getKey: (id) => id,
    applyOptimistic: (id) => applyStatus(id, 'resolved'),
    rollback: (id) => rollbackStatus(id),
    successMessage: 'Incidente resuelto',
    errorMessage: 'No se pudo resolver el incidente',
  });

  const acknowledge = async (id: string) => {
    await ackMutation.mutateAsync(id);
    refresh();
  };

  const resolve = async (id: string) => {
    await resolveMutation.mutateAsync(id);
    refresh();
  };

  const pendingIds = new Set<string>([
    ...ackMutation.pendingKeys,
    ...resolveMutation.pendingKeys,
  ]);

  return { incidents, loading, refresh, create, acknowledge, resolve, pendingIds };
}
